/**
 * Renders the navigation menu component
 */
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { useCallback, useState } from 'react';

import { Colors } from '@nakedplatform/ui';

import { Routes } from 'routes';
import { NakedIcon } from 'theme/icons';

import { Image, Images } from 'theme/images';
import { DEFAULT_OPTIONS } from './constants';
import { DropDown } from './dropdown';
import { SocialMediaLinks } from './socialMedia';
import {
  BurgerMenuLine,
  LogoLink,
  MenuButton,
  MenuCloseButton,
  MenuContainer,
  MenuItem,
  MenuLink,
  MenuList,
  MenuOverlay,
  NavSection,
  StickyNav,
} from './styles';
import { Social } from './social';

const NAKED_LOGO_SIZE = 24;
const NAV_ACTION_PLACEHOLDER_STYLES = {
  width: '160px',
  height: '52px',
  marginRight: '20px',
} as const;

const LatestQuoteButton = dynamic(() => import('modules/prospects/components/latest-quote'), {
  ssr: false,

  // eslint-disable-next-line react/display-name
  loading: () => <div aria-hidden style={NAV_ACTION_PLACEHOLDER_STYLES} />,
});

/**
 * Top Menu on page
 */
export function Menu() {
  const [isSideMenuOpen, toggleSideMenu] = useState(false);

  const handleOpenSideMenu = useCallback(() => {
    toggleSideMenu(!isSideMenuOpen);
  }, [isSideMenuOpen]);

  return (
    <StickyNav>
      <NavSection as="nav">
        <MenuButton className="hide-for-medium" onClick={handleOpenSideMenu}>
          <BurgerMenuLine />
          <BurgerMenuLine />
          <BurgerMenuLine />
        </MenuButton>
        <LogoLink href={Routes.HOME}>
          <NakedIcon name="naked-logo" size={NAKED_LOGO_SIZE} color={Colors.thunder} />
        </LogoLink>
        <MenuOverlay
          opacity={isSideMenuOpen ? 1 : 0}
          display={isSideMenuOpen ? 'block' : 'none'}
          onClick={handleOpenSideMenu}
        />
        <MenuContainer>
          <MenuList translateX={isSideMenuOpen ? '0%' : '-100%'}>
            <MenuItem className="hide-for-medium">
              <MenuLink
                href={Routes.HOME}
                aria-label="home page link"
                style={{ marginTop: '-24px', marginBottom: '24px' }}
                onClick={handleOpenSideMenu}
              >
                <NakedIcon name="naked-logo" size={NAKED_LOGO_SIZE} color={Colors.white} />
              </MenuLink>
            </MenuItem>
            <MenuItem>
              <MenuLink href={Routes.CAR_INSURANCE} onClick={handleOpenSideMenu} isBold={true}>
                Car
              </MenuLink>
            </MenuItem>
            <MenuItem position="relative">
              <MenuLink as="span" hideOnMobile={true} isDropdownLink>
                Home
                <Image src={Images['dropDownArrow']} marginLeft={2} size={10} />
              </MenuLink>

              <DropDown>
                {DEFAULT_OPTIONS.dropdown.map(dropdown => (
                  <DropDown.Item
                    onClick={handleOpenSideMenu}
                    url={dropdown.url}
                    key={dropdown.url}
                    comingSoon={dropdown.comingSoon}
                    migrated={dropdown.migrated}
                    isSideMenuOpen={isSideMenuOpen}
                  >
                    {dropdown.title}
                  </DropDown.Item>
                ))}
              </DropDown>
            </MenuItem>
            <MenuItem>
              <MenuLink onClick={handleOpenSideMenu} href={Routes.PET_INSURANCE}>
                Pet
              </MenuLink>
            </MenuItem>
            {DEFAULT_OPTIONS.hiddenItems.map(item => {
              const menuLink = (
                <MenuLink onClick={handleOpenSideMenu} href={item.url}>
                  {item.title}
                </MenuLink>
              );

              return (
                <MenuItem key={item.url} className="hide-for-medium">
                  {item.migrated ? (
                    menuLink
                  ) : (
                    <Link href={item.url} prefetch={false}>
                      {menuLink}
                    </Link>
                  )}
                </MenuItem>
              );
            })}

            <MenuItem className="hide-for-medium">
              <Social>
                <SocialMediaLinks />
              </Social>
            </MenuItem>

            <MenuCloseButton onClick={handleOpenSideMenu} aria-label="close menu button">
              <NakedIcon name="close" size={12} color={Colors.white} />
            </MenuCloseButton>
          </MenuList>
          <LatestQuoteButton />
        </MenuContainer>
      </NavSection>
    </StickyNav>
  );
}
